import React, { useState, useEffect } from 'react';
import { apiService, departmentAPI, shiftAPI } from '../services/api';
import { getFirebaseToken } from '../services/authService';
import { Machine } from '../types/machine';
import { Department, Shift } from '../types/operations';
import AlertsPanel from '../components/AlertsPanel';
import PassdownModal from '../components/PassdownModal';

interface MachineDataRecord {
  id: number;
  jobId: string;
  partId: string;
  machineId: number;  // Foreign key to machines table
  machineIdentifier: string;  // String identifier like "MC-110"
  machineName: string;  // Machine name like "RAZOR"
  operator: string;
  goodQty: number;
  scrapQty: number;
  downtimeMin: number;
  date: string;
  shiftName: string;
}

interface MachineGroup {
  machineDbId: number;  // Database ID (foreign key)
  machineId: string;  // String identifier for display
  machineName: string;  // Machine name
  jobs: MachineDataRecord[];
  totalGood: number;
  totalScrap: number;
  totalDown: number;
  scrapRate: number;
  eff: number;
  hasScrapAlert: boolean;
  hasDowntimeAlert: boolean;
  hasAlert: boolean;
  hasData: boolean;
}

// Helper to get date in YYYY-MM-DD format
const getToday = (): string => {
  return new Date().toISOString().split('T')[0];
};


const Dashboard: React.FC = () => {
  const [data, setData] = useState<MachineDataRecord[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [scrapThreshold, setScrapThreshold] = useState(5);
  const [downtimeThreshold, setDowntimeThreshold] = useState(30);
  const [expandedMachines, setExpandedMachines] = useState<Set<number>>(new Set());
  const [loadingMachines, setLoadingMachines] = useState(true);
  const [, setLoadingData] = useState(true);
  const [kpiAlertsPanelCollapsed, setKpiAlertsPanelCollapsed] = useState(false);
  const [showPassdownModal, setShowPassdownModal] = useState(false);

  // Filter state - default to today
  const [filterMachineIds, setFilterMachineIds] = useState<number[]>([]);
  const [filterDateFrom, setFilterDateFrom] = useState<string>(getToday());
  const [filterDateTo, setFilterDateTo] = useState<string>(getToday());
  const [filterShiftName, setFilterShiftName] = useState<string>('');
  const [filterDepartmentId, setFilterDepartmentId] = useState<number | ''>('');
  const [departments, setDepartments] = useState<Department[]>([]);
  const [configuredShifts, setConfiguredShifts] = useState<Shift[]>([]);
  const [machineDropdownOpen, setMachineDropdownOpen] = useState(false);

  const fetchDepartments = async () => {
    try {
      const depts = await departmentAPI.list();
      setDepartments(depts);
    } catch (error: any) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchConfiguredShifts = async () => {
    try {
      // Fetch shifts for the selected department, or all shifts
      const deptId = filterDepartmentId || undefined;
      const shifts = await shiftAPI.list(deptId ? Number(deptId) : undefined);
      setConfiguredShifts(shifts);
    } catch (error: any) {
      console.error('Error fetching shifts:', error);
    }
  };

  useEffect(() => {
    fetchMachines();
    fetchMachineData();
    fetchDepartments();
    fetchConfiguredShifts();
  }, []);

  // Refetch shifts when department changes
  useEffect(() => {
    fetchConfiguredShifts();
    // Clear shift filter when department changes
    setFilterShiftName('');
  }, [filterDepartmentId]);

  const fetchMachines = async () => {
    try {
      setLoadingMachines(true);
      const fetchedMachines = await apiService.machines.list();
      setMachines(fetchedMachines);
    } catch (error: any) {
      console.error('Error fetching machines:', error);
    } finally {
      setLoadingMachines(false);
    }
  };

  const fetchMachineData = async () => {
    try {
      setLoadingData(true);
      const backendData = await apiService.machineData.list();

      // Transform backend data to match frontend interface
      const transformedData: MachineDataRecord[] = backendData.map(item => ({
        id: item.id,
        jobId: item.job_id || '',
        partId: item.part_id || '',
        machineId: item.machine_id,  // Integer FK to machines table
        machineIdentifier: item.machine_identifier || '',  // String like "MC-110"
        machineName: item.machine_name || '',  // Name like "RAZOR"
        operator: item.operator || '',
        goodQty: item.good_qty || 0,
        scrapQty: item.scrap_qty || 0,
        downtimeMin: item.downtime_min || 0,
        date: item.production_date || '',
        shiftName: item.shift_name || '',
      }));

      setData(transformedData);
    } catch (error: any) {
      console.error('Error fetching machine data:', error);
    } finally {
      setLoadingData(false);
    }
  };

  const handleExportPdf = async () => {
    try {
      // Build query params based on current filters
      const params = new URLSearchParams();

      if (filterMachineIds.length > 0) {
        params.append('machine_ids', filterMachineIds.join(','));
      }
      if (filterDateFrom) {
        params.append('date_from', filterDateFrom);
      }
      if (filterDateTo) {
        params.append('date_to', filterDateTo);
      }
      params.append('scrap_threshold', scrapThreshold.toString());
      params.append('downtime_threshold', downtimeThreshold.toString());

      // Get auth token (Firebase first, then legacy fallback)
      const token = await getFirebaseToken() || localStorage.getItem('access_token');
      if (!token) {
        alert('Please log in to export PDF');
        return;
      }

      // Fetch PDF from backend
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:9000/api/v1'}/dashboard/pdf?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }

      // Download the PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ProductionDashboard_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export PDF. Please try again.');
    }
  };

  // Calculate metrics
  const rowScrapPct = (r: MachineDataRecord) => {
    const total = r.goodQty + r.scrapQty;
    return total ? (r.scrapQty / total * 100) : 0;
  };

  const groupByMachine = (rows: MachineDataRecord[]): MachineGroup[] => {
    const machineMap: { [key: number]: MachineGroup } = {};

    // Apply filters to rows
    let filteredRows = rows;

    // Filter by machine IDs if any selected
    if (filterMachineIds.length > 0) {
      filteredRows = filteredRows.filter(r => filterMachineIds.includes(r.machineId));
    }

    // Filter by date range if set
    if (filterDateFrom) {
      filteredRows = filteredRows.filter(r => r.date >= filterDateFrom);
    }
    if (filterDateTo) {
      filteredRows = filteredRows.filter(r => r.date <= filterDateTo);
    }

    // Filter by shift name if selected
    if (filterShiftName) {
      filteredRows = filteredRows.filter(r => r.shiftName === filterShiftName);
    }

    // Initialize machines - filter by department and/or selected machines
    let activeMachines = machines.filter(m => m.is_active);
    if (filterDepartmentId) {
      activeMachines = activeMachines.filter(m => m.department_id === Number(filterDepartmentId));
    }
    const machinesToShow = filterMachineIds.length > 0
      ? activeMachines.filter(m => filterMachineIds.includes(m.id))
      : activeMachines;

    machinesToShow.forEach(machine => {
      machineMap[machine.id] = {
        machineDbId: machine.id,
        machineId: machine.machine_id,
        machineName: machine.machine_name,
        jobs: [],
        totalGood: 0,
        totalScrap: 0,
        totalDown: 0,
        scrapRate: 0,
        eff: 0,
        hasScrapAlert: false,
        hasDowntimeAlert: false,
        hasAlert: false,
        hasData: false
      };
    });

    // Build set of allowed machine IDs for department filtering
    const allowedMachineIds = new Set(machinesToShow.map(m => m.id));

    // Add data to machines using database ID
    filteredRows.forEach(r => {
      if (!machineMap[r.machineId]) {
        // Skip data rows for machines not in the filtered set (e.g., different department)
        if (filterDepartmentId && !allowedMachineIds.has(r.machineId)) return;
        machineMap[r.machineId] = {
          machineDbId: r.machineId,
          machineId: r.machineIdentifier,
          machineName: r.machineName,
          jobs: [],
          totalGood: 0,
          totalScrap: 0,
          totalDown: 0,
          scrapRate: 0,
          eff: 0,
          hasScrapAlert: false,
          hasDowntimeAlert: false,
          hasAlert: false,
          hasData: false
        };
      }

      machineMap[r.machineId].jobs.push(r);
      machineMap[r.machineId].totalGood += r.goodQty;
      machineMap[r.machineId].totalScrap += r.scrapQty;
      machineMap[r.machineId].totalDown += r.downtimeMin;
      machineMap[r.machineId].hasData = true;
    });

    return Object.values(machineMap).map(m => {
      const total = m.totalGood + m.totalScrap;
      m.scrapRate = total ? (m.totalScrap / total * 100) : 0;
      m.eff = total ? (m.totalGood / total * 100) : 0;
      m.hasScrapAlert = m.scrapRate > scrapThreshold;
      m.hasDowntimeAlert = m.totalDown > downtimeThreshold;
      m.hasAlert = m.hasScrapAlert || m.hasDowntimeAlert;
      return m;
    }).sort((a, b) => {
      // Sort: alerts first, then no data, then good
      if (a.hasAlert !== b.hasAlert) return a.hasAlert ? -1 : 1;
      if (a.hasData !== b.hasData) return a.hasData ? -1 : 1;
      return a.machineId.localeCompare(b.machineId);
    });
  };

  const machineGroups = groupByMachine(data);
  const totalGood = machineGroups.reduce((a, m) => a + m.totalGood, 0);
  const totalScrap = machineGroups.reduce((a, m) => a + m.totalScrap, 0);
  const totalDowntime = machineGroups.reduce((a, m) => a + m.totalDown, 0);
  const overallScrap = (totalGood + totalScrap) > 0
    ? ((totalScrap / (totalGood + totalScrap)) * 100).toFixed(1)
    : '0.0';
  const avgEff = machineGroups.filter(m => m.hasData).length
    ? (machineGroups.filter(m => m.hasData).reduce((a, m) => a + m.eff, 0) / machineGroups.filter(m => m.hasData).length).toFixed(1)
    : '0.0';

  // Show welcome screen if no machines are configured
  if (!loadingMachines && machines.length === 0) {
    return (
      <section className="p-6">
        <div className="max-w-2xl mx-auto mt-12 text-center">
          <div className="rounded-2xl shadow-xl p-8 border" style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
            <div className="text-6xl mb-4">🏭</div>
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              Welcome to Vellrix
            </h2>
            <p className="text-lg mb-4" style={{ color: 'var(--text-secondary)' }}>
              Get started by adding your first machine to begin tracking production data.
            </p>
            <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
              Need to set up plants, departments, or shifts first? Visit{' '}
              <a href="/app#operations" className="text-blue-600 hover:text-blue-700 underline font-medium">
                Settings → Operations
              </a>
              {' '}to configure your operational structure.
            </p>
          </div>
        </div>
      </section>
    );
  }

  const toggleMachine = (machineDbId: number) => {
    const newExpanded = new Set(expandedMachines);
    if (newExpanded.has(machineDbId)) {
      newExpanded.delete(machineDbId);
    } else {
      newExpanded.add(machineDbId);
    }
    setExpandedMachines(newExpanded);
  };

  // Get tile border color based on status
  const getTileStyle = (m: MachineGroup) => {
    if (m.hasAlert) {
      return 'border-red-500 bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/30 dark:to-red-800/20';
    }
    if (!m.hasData) {
      return 'border-yellow-400 bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/30 dark:to-yellow-800/20';
    }
    return 'border-green-500 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/20';
  };

  // Progress bar component for visual metrics
  const ProgressBar: React.FC<{ value: number; max: number; color: string; label?: string }> = ({ value, max, color, label }) => {
    const percentage = Math.min((value / max) * 100, 100);
    return (
      <div className="w-full">
        {label && <div className="text-xs text-gray-500 mb-1">{label}</div>}
        <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${color}`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    );
  };

  return (
    <section className="p-6 space-y-5">
      {/* Header with action buttons */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
          Production Dashboard
        </h1>
        <div className="flex gap-2">
          <button
            onClick={handleExportPdf}
            className="px-4 py-2 rounded-lg font-medium border-2 transition-all hover:shadow-md"
            style={{ borderColor: 'var(--border-color)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
          >
            📄 Export PDF
          </button>
          <button
            onClick={() => setShowPassdownModal(true)}
            className="px-4 py-2 rounded-lg font-semibold bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800 transition-all shadow-md hover:shadow-lg"
          >
            📧 Send Passdown
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-xl shadow-lg p-4 border-2" style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
        <div className="flex flex-wrap gap-4 items-center">
          <div className="text-sm font-bold uppercase tracking-wide" style={{ color: 'var(--text-secondary)' }}>Filters</div>

          {/* Department Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Dept:</span>
            <select
              value={filterDepartmentId}
              onChange={(e) => {
                setFilterDepartmentId(e.target.value ? Number(e.target.value) : '');
                setFilterMachineIds([]); // Reset machine filter when dept changes
              }}
              className="border-2 rounded-lg px-3 py-2 text-sm hover:border-blue-400 transition-colors"
              style={{
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                borderColor: 'var(--border-color)',
                minWidth: '160px'
              }}
            >
              <option value="">All Departments</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
          </div>

          {/* Multi-select Machine Dropdown */}
          <div className="relative">
            <div
              onClick={() => setMachineDropdownOpen(!machineDropdownOpen)}
              className="border-2 rounded-lg px-3 py-2 min-w-[220px] cursor-pointer flex items-center gap-2 flex-wrap hover:border-blue-400 transition-colors"
              style={{
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                borderColor: 'var(--border-color)',
                minHeight: '42px',
                fontSize: '14px'
              }}
            >
              {filterMachineIds.length === 0 ? (
                <span style={{ color: 'var(--text-secondary)' }}>All Machines</span>
              ) : (
                <>
                  {filterMachineIds.map(id => {
                    const machine = machines.find(m => m.id === id);
                    return machine ? (
                      <span
                        key={id}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                        style={{ fontSize: '12px' }}
                      >
                        {machine.machine_id}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setFilterMachineIds(filterMachineIds.filter(mid => mid !== id));
                          }}
                          className="hover:text-blue-600 dark:hover:text-blue-100 ml-1"
                          style={{ fontSize: '14px', lineHeight: '1' }}
                        >
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                </>
              )}
              <span className="ml-auto text-gray-400">▼</span>
            </div>

            {machineDropdownOpen && (
              <div
                className="absolute z-10 mt-1 w-full border-2 rounded-lg shadow-xl max-h-60 overflow-auto"
                style={{
                  background: 'var(--bg-secondary)',
                  borderColor: 'var(--border-color)',
                  minWidth: '260px'
                }}
              >
                {machines.filter(m => m.is_active && (!filterDepartmentId || m.department_id === Number(filterDepartmentId))).map(machine => (
                  <div
                    key={machine.id}
                    onClick={() => {
                      if (filterMachineIds.includes(machine.id)) {
                        setFilterMachineIds(filterMachineIds.filter(id => id !== machine.id));
                      } else {
                        setFilterMachineIds([...filterMachineIds, machine.id]);
                      }
                    }}
                    className="px-3 py-2 cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/30 flex items-center gap-3 transition-colors"
                    style={{ color: 'var(--text-primary)', fontSize: '14px' }}
                  >
                    <input
                      type="checkbox"
                      checked={filterMachineIds.includes(machine.id)}
                      onChange={() => {}}
                      className="pointer-events-none w-4 h-4 rounded"
                    />
                    <span className="font-medium">{machine.machine_id}</span>
                    <span className="text-gray-500">- {machine.machine_name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Date:</span>
            <input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              className="border-2 rounded-lg px-3 py-2 text-sm hover:border-blue-400 transition-colors"
              style={{
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                borderColor: 'var(--border-color)'
              }}
            />
            <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>to</span>
            <input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              className="border-2 rounded-lg px-3 py-2 text-sm hover:border-blue-400 transition-colors"
              style={{
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                borderColor: 'var(--border-color)'
              }}
            />
          </div>

          {/* Shift Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Shift:</span>
            <select
              value={filterShiftName}
              onChange={(e) => setFilterShiftName(e.target.value)}
              className="border-2 rounded-lg px-3 py-2 text-sm hover:border-blue-400 transition-colors"
              style={{
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                borderColor: 'var(--border-color)',
                minWidth: '140px'
              }}
            >
              <option value="">All Shifts</option>
              {configuredShifts.map(shift => (
                <option key={shift.id} value={shift.name}>{shift.name}</option>
              ))}
            </select>
          </div>

          <button
            onClick={() => {
              setFilterMachineIds([]);
              setFilterDateFrom(getToday());
              setFilterDateTo(getToday());
              setFilterShiftName('');
              setFilterDepartmentId('');
            }}
            className="px-4 py-2 rounded-lg font-medium border-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
            style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
          >
            Reset
          </button>
        </div>
      </div>

      {/* Click outside to close dropdown */}
      {machineDropdownOpen && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setMachineDropdownOpen(false)}
        />
      )}

      {/* KPI Alerts Panel (OpActs) */}
      <AlertsPanel
        collapsed={kpiAlertsPanelCollapsed}
        onToggleCollapse={() => setKpiAlertsPanelCollapsed(!kpiAlertsPanelCollapsed)}
        dateFrom={filterDateFrom}
        dateTo={filterDateTo}
        departmentId={filterDepartmentId}
        shiftName={filterShiftName}
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="rounded-xl shadow-lg p-5 border-2 border-transparent hover:border-blue-300 transition-all" style={{ background: 'var(--bg-secondary)' }}>
          <div className="text-sm font-medium uppercase tracking-wide mb-1" style={{ color: 'var(--text-secondary)' }}>
            {filterMachineIds.length > 0 ? 'Filtered Machines' : 'Active Machines'}
          </div>
          <div className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
            {machineGroups.length}
          </div>
          <div className="mt-2">
            <ProgressBar
              value={machineGroups.filter(m => m.hasData).length}
              max={machineGroups.length || 1}
              color="bg-blue-500"
              label={`${machineGroups.filter(m => m.hasData).length} with data`}
            />
          </div>
        </div>

        <div className="rounded-xl shadow-lg p-5 border-2 border-transparent hover:border-green-300 transition-all" style={{ background: 'var(--bg-secondary)' }}>
          <div className="text-sm font-medium uppercase tracking-wide mb-1" style={{ color: 'var(--text-secondary)' }}>
            Total Good
          </div>
          <div className="text-3xl font-bold text-green-600">
            {totalGood.toLocaleString()}
          </div>
          <div className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>
            {totalScrap.toLocaleString()} scrap units
          </div>
        </div>

        <div className="rounded-xl shadow-lg p-5 border-2 border-transparent hover:border-orange-300 transition-all" style={{ background: 'var(--bg-secondary)' }}>
          <div className="text-sm font-medium uppercase tracking-wide mb-1" style={{ color: 'var(--text-secondary)' }}>
            Overall Scrap %
          </div>
          <div className={`text-3xl font-bold ${parseFloat(overallScrap) > scrapThreshold ? 'text-red-600' : 'text-green-600'}`}>
            {overallScrap}%
          </div>
          <div className="mt-2">
            <ProgressBar
              value={parseFloat(overallScrap)}
              max={scrapThreshold * 2}
              color={parseFloat(overallScrap) > scrapThreshold ? 'bg-red-500' : 'bg-green-500'}
              label={`Threshold: ${scrapThreshold}%`}
            />
          </div>
        </div>

        <div className="rounded-xl shadow-lg p-5 border-2 border-transparent hover:border-purple-300 transition-all" style={{ background: 'var(--bg-secondary)' }}>
          <div className="text-sm font-medium uppercase tracking-wide mb-1" style={{ color: 'var(--text-secondary)' }}>
            Avg Efficiency
          </div>
          <div className={`text-3xl font-bold ${parseFloat(avgEff) >= 95 ? 'text-green-600' : parseFloat(avgEff) >= 85 ? 'text-yellow-600' : 'text-red-600'}`}>
            {avgEff}%
          </div>
          <div className="mt-2">
            <ProgressBar
              value={parseFloat(avgEff)}
              max={100}
              color={parseFloat(avgEff) >= 95 ? 'bg-green-500' : parseFloat(avgEff) >= 85 ? 'bg-yellow-500' : 'bg-red-500'}
              label={`${totalDowntime} min total downtime`}
            />
          </div>
        </div>
      </div>

      {/* Machine Performance */}
      <div className="rounded-xl shadow-lg p-6 border-2" style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
        <div className="font-bold text-xl mb-4" style={{ color: 'var(--text-primary)' }}>
          Machine Performance
        </div>
        {machineGroups.length === 0 ? (
          <div className="text-center py-12" style={{ color: 'var(--text-secondary)' }}>
            <div className="text-5xl mb-4">📊</div>
            <div className="text-lg font-medium">No data to display</div>
            <div className="text-sm mt-1">Upload data or adjust filters to see results</div>
          </div>
        ) : (
          <div className="grid gap-4">
            {machineGroups.map(m => (
              <div
                key={m.machineDbId}
                className={`border-2 rounded-xl overflow-hidden transition-all hover:shadow-lg ${getTileStyle(m)}`}
              >
                <div
                  className="p-4 cursor-pointer"
                  onClick={() => toggleMachine(m.machineDbId)}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <span className="text-gray-400 transition-transform" style={{ transform: expandedMachines.has(m.machineDbId) ? 'rotate(90deg)' : 'none' }}>
                        ▶
                      </span>
                      <div>
                        <div className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>
                          {m.machineId} - {m.machineName}
                        </div>
                        {!m.hasData && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-200 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200">
                            No data
                          </span>
                        )}
                      </div>
                      {m.hasAlert ? (
                        <span className="text-red-600 text-2xl">⚠️</span>
                      ) : m.hasData ? (
                        <span className="text-green-600 text-2xl">✅</span>
                      ) : null}
                    </div>

                    {m.hasData && (
                      <div className="flex gap-6 text-sm">
                        <div className="text-center">
                          <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Efficiency</div>
                          <div className={`font-bold text-lg ${m.eff >= 95 ? 'text-green-600' : m.eff >= 85 ? 'text-yellow-600' : 'text-red-600'}`}>
                            {m.eff.toFixed(1)}%
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Output</div>
                          <div className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>{m.totalGood.toLocaleString()}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Scrap</div>
                          <div className={`font-bold text-lg ${m.hasScrapAlert ? 'text-red-600' : 'text-gray-600'}`}>
                            {m.scrapRate.toFixed(1)}%
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Downtime</div>
                          <div className={`font-bold text-lg ${m.hasDowntimeAlert ? 'text-red-600' : 'text-gray-600'}`}>
                            {m.totalDown}m
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Mini progress bars for quick visual */}
                  {m.hasData && (
                    <div className="mt-3 grid grid-cols-3 gap-4">
                      <ProgressBar value={m.eff} max={100} color={m.eff >= 95 ? 'bg-green-500' : m.eff >= 85 ? 'bg-yellow-500' : 'bg-red-500'} label="Efficiency" />
                      <ProgressBar value={m.scrapRate} max={scrapThreshold * 2} color={m.hasScrapAlert ? 'bg-red-500' : 'bg-green-500'} label="Scrap" />
                      <ProgressBar value={m.totalDown} max={downtimeThreshold * 2} color={m.hasDowntimeAlert ? 'bg-red-500' : 'bg-green-500'} label="Downtime" />
                    </div>
                  )}
                </div>

                {expandedMachines.has(m.machineDbId) && (
                  <div className="p-4 pt-0 border-t" style={{ borderColor: 'rgba(0,0,0,0.1)' }}>
                    {m.jobs.length === 0 ? (
                      <div className="text-center py-8 px-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg mt-3">
                        <div className="text-gray-400 dark:text-gray-500 mb-2">
                          <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <p className="text-gray-500 dark:text-gray-400 font-medium">No production data available</p>
                        <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">This machine is configured but has no recorded data for the selected date range.</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto mt-3">
                        <table className="w-full border-collapse text-sm rounded-lg overflow-hidden">
                          <thead>
                            <tr className="bg-gray-100 dark:bg-gray-800">
                              <th className="border border-gray-200 dark:border-gray-700 p-3 text-left font-bold" style={{ width: '30px' }}></th>
                              <th className="border border-gray-200 dark:border-gray-700 p-3 text-left font-bold">Date</th>
                              <th className="border border-gray-200 dark:border-gray-700 p-3 text-right font-bold">Good</th>
                              <th className="border border-gray-200 dark:border-gray-700 p-3 text-right font-bold">Scrap</th>
                              <th className="border border-gray-200 dark:border-gray-700 p-3 text-right font-bold">Scrap %</th>
                              <th className="border border-gray-200 dark:border-gray-700 p-3 text-right font-bold">Downtime</th>
                            </tr>
                          </thead>
                          <tbody>
                            {m.jobs.map(j => {
                              const sp = rowScrapPct(j);
                              const scrapViolation = sp > scrapThreshold;
                              const downtimeViolation = j.downtimeMin > downtimeThreshold;
                              const viol = scrapViolation || downtimeViolation;
                              return (
                                <tr key={j.id} className={viol ? 'bg-red-50 dark:bg-red-900/20' : 'bg-white dark:bg-gray-800/50'}>
                                  <td className="border border-gray-200 dark:border-gray-700 p-3 text-center">
                                    {viol ? '🔴' : ''}
                                  </td>
                                  <td className="border border-gray-200 dark:border-gray-700 p-3 font-medium">{j.date}</td>
                                  <td className="border border-gray-200 dark:border-gray-700 p-3 text-right font-medium">{j.goodQty.toLocaleString()}</td>
                                  <td className="border border-gray-200 dark:border-gray-700 p-3 text-right">{j.scrapQty.toLocaleString()}</td>
                                  <td className={`border border-gray-200 dark:border-gray-700 p-3 text-right font-bold ${scrapViolation ? 'text-red-600' : ''}`}>
                                    {sp.toFixed(1)}%
                                  </td>
                                  <td className={`border border-gray-200 dark:border-gray-700 p-3 text-right font-bold ${downtimeViolation ? 'text-red-600' : ''}`}>
                                    {j.downtimeMin} min
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Passdown Modal */}
      <PassdownModal
        isOpen={showPassdownModal}
        onClose={() => setShowPassdownModal(false)}
        defaultShiftName={filterShiftName}
      />
    </section>
  );
};

export default Dashboard;
